import { channelClient, RequestPolicies } from 'shared/channels';
import { createEntityClient, runQueuedMutation } from 'shared/channels/entity';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import type { InspectionInDto } from 'shared/dtos/in/inspection';
import type { ProtocolAnswerInDto } from 'shared/dtos/in/protocolAnswer';
import type { ProtocolItemInDto } from 'shared/dtos/in/protocolItem';
import type { ProtocolSectionInDto } from 'shared/dtos/in/protocolSection';
import type { ProtocolSectionAnswersBatchResult } from 'shared/dtos/out/protocolAnswer';
import type { InspectionModel } from './inspectionModel';
import {
  expectOutcome,
  isInspectionOutcome,
  isProtocolAnswerOutcome,
  isProtocolItemOutcome,
  isProtocolSectionAnswersOutcome,
  isProtocolSectionOutcome,
} from './inspectionOutcomes';
import { inspectionContract, type InspectionRequests } from './inspectionContract';

/** A change as the page describes it; the client adds the `uniqueId` correlating its outcome. */
export type InspectionChange<TMethod extends keyof InspectionRequests> = Omit<
  InspectionRequests[TMethod]['request'],
  'uniqueId'
>;

/** The inspection a change is about, in its project. */
export type InspectionRef = InspectionChange<'remove'>;

/** Every inspection and protocol change except a create is answered with `updatedEntity`. */
const updated = DomainMessagesTypes.updatedEntity;

/**
 * Inspections of the selected project as served by the service worker (none
 * for standard users), and the filling of their protocols. Changes resolve
 * with what the server saved and reject with its error
 * (`ChannelRequestError.messageId`). A caller that no longer needs an answer
 * (the user changed the field again) ignores it: changes are never cancelled,
 * the server saves each of them.
 */
export const inspectionClient = {
  ...createEntityClient(inspectionContract),

  /** One inspection with its protocol, archived ones included; rejects when there is none. */
  getOneById(id: string): Promise<InspectionModel> {
    return channelClient.request(inspectionContract, 'getOneById', { id }, RequestPolicies.read);
  },

  create(change: InspectionChange<'create'>): Promise<InspectionInDto> {
    return runQueuedMutation(
      inspectionContract,
      'create',
      (uniqueId) => ({ ...change, uniqueId }),
      DomainMessagesTypes.uploadedEntity
    );
  },

  async edit(change: InspectionChange<'edit'>): Promise<InspectionInDto> {
    const outcome = await runQueuedMutation(inspectionContract, 'edit', (uniqueId) => ({ ...change, uniqueId }), updated);
    return expectOutcome(outcome, isInspectionOutcome, 'edit');
  },

  async remove(inspection: InspectionRef): Promise<void> {
    await runQueuedMutation(
      inspectionContract,
      'remove',
      (uniqueId) => ({ ...inspection, uniqueId }),
      DomainMessagesTypes.deletedEntity
    );
  },

  async restore(inspection: InspectionRef): Promise<void> {
    await runQueuedMutation(inspectionContract, 'restore', (uniqueId) => ({ ...inspection, uniqueId }), updated);
  },

  async editProtocolItem(change: InspectionChange<'editProtocolItem'>): Promise<ProtocolItemInDto> {
    const outcome = await runQueuedMutation(
      inspectionContract,
      'editProtocolItem',
      (uniqueId) => ({ ...change, uniqueId }),
      updated
    );
    return expectOutcome(outcome, isProtocolItemOutcome, 'editProtocolItem');
  },

  /** Long running, as the legacy answer save: the answers of a protocol wait for each other in the HTTP queue. */
  async editProtocolAnswer(change: InspectionChange<'editProtocolAnswer'>): Promise<ProtocolAnswerInDto> {
    const outcome = await runQueuedMutation(
      inspectionContract,
      'editProtocolAnswer',
      (uniqueId) => ({ ...change, uniqueId }),
      updated,
      { policy: RequestPolicies.longRunning }
    );
    return expectOutcome(outcome, isProtocolAnswerOutcome, 'editProtocolAnswer');
  },

  async editProtocolSection(change: InspectionChange<'editProtocolSection'>): Promise<Partial<ProtocolSectionInDto>> {
    const outcome = await runQueuedMutation(
      inspectionContract,
      'editProtocolSection',
      (uniqueId) => ({ ...change, uniqueId }),
      updated
    );
    return expectOutcome(outcome, isProtocolSectionOutcome, 'editProtocolSection');
  },

  /** Sets the applicability of every answer in the section tree; long running like an answer. */
  async editProtocolSectionAnswers(
    change: InspectionChange<'editProtocolSectionAnswers'>
  ): Promise<ProtocolSectionAnswersBatchResult> {
    const outcome = await runQueuedMutation(
      inspectionContract,
      'editProtocolSectionAnswers',
      (uniqueId) => ({ ...change, uniqueId }),
      updated,
      { policy: RequestPolicies.longRunning }
    );
    return expectOutcome(outcome, isProtocolSectionAnswersOutcome, 'editProtocolSectionAnswers');
  },
};
